import {
  ConfirmDialog,
  CustomButton,
  MultiSelectSearchDropDown,
  PageTitle,
  SvgInfo,
  SvgVerticalSplitter,
} from '@ui-kit'
import {
  HTTP_STATUS_CONSTANTS,
  NAMESPACES,
  PERMISSION_ASSIGNMENT,
  PERMISSION_MODULES,
} from '../../utils/constants'
import { AssignPermissionSet } from './AssignPermissionSet'
import { useEffect, useState, useRef } from 'react'
import {
  DropdownType,
  savePermissionSetsForUsersPayloadType,
  useDispatch,
  addToaster,
  savePermissionSetsForUsers,
  showLoader,
  hideLoader,
  ProgramPermissionSetType,
  DataTypeStore,
  getProgramsList,
  getProgramsAndPermissionSets,
  getUnassignedUsers,
  getUnassignedUsersForGlobal,
  PermissionSetType,
  saveGlobalPermissionSetsForUsers,
  setClientDropdown,
} from '@app-store'
import { noop, uniqueId } from 'lodash'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { GlobalError } from 'react-hook-form'
import { RootState, useAppSelector } from '../../utils/agent.store'
import { HCPProgramType } from '../../types/permissionAssignment.types'
import { programPermissionsObject } from '../../utils/helperFunctions'

export const AssignPermissions = (props: {
  actionMode: string
  isGlobalSet: boolean
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { userId } = useParams()
  const [users, setUsers] = useState<
    { label: string; value: string; subLabel: string }[]
  >([])
  const [programOptions, setProgramOptions] = useState<
    DropdownType<DataTypeStore>[]
  >([])
  const [dropdownOptions, setDropdownOptions] = useState<
    { label: string; value: string; subLabel: string }[]
  >([])
  const [programPermissionSets, setProgramPermissionSets] = useState<
    ProgramPermissionSetType[]
  >([])
  const [selectedUsers, setSelectedUsers] = useState<DropdownType<string>[]>([])
  const [isEditEnabled, setIsEditEnabled] = useState(true)
  const [errors, setErrors] = useState<Record<string, GlobalError>>()
  const [conflictedClients, setConflictedClients] = useState<
    Record<string, string[]>
  >({})
  const isInitialLoad = useRef(true)
  const [renderKey, setRenderKey] = useState(false)
  const initialModalData = { open: false, message: '', onConfirm: () => noop }
  const [modal, setModal] = useState<{
    message: string
    onConfirm: () => void
    open: boolean
  }>(initialModalData)
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const programWithPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programs
  )
  const selectedTenantName = useAppSelector(
    (state) => state.authUser.selectedClientTenant?.name_of_organization
  )
  const isSuperAdmin = useAppSelector(
    (state: RootState) => state.authUser.user?.superAdmin
  )
  const [checkedPermissionSets, setCheckedPermissionSets] = useState<
    Record<string, PermissionSetType>
  >({})
  const divRef = useRef<HTMLDivElement | null>(null)
  const viewFields = useRef({
    userId: '',
    firstName: '',
    lastName: '',
  })

  const fetchPrograms = async () => {
    if (isSuperAdmin) {
      try {
        const programs = await getProgramsList()
        if (programs?.data?.content) {
          const programOptionsList: DropdownType<DataTypeStore>[] =
            programs?.data?.content
              ?.filter((item: HCPProgramType) => item.enabled)
              ?.map((item: HCPProgramType) => {
                return {
                  label: item?.operational_program_name ?? '',
                  value: item?.id?.toString() ?? '',
                }
              })
          setProgramOptions([...programOptionsList])
        }
      } catch (err) {
        console.log(err)
        setProgramOptions([])
      }
    } else {
      const programIdsWithPermission =
        programPermissionsObject.Permission_Set_Assignment_View(
          programPermissions,
          PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
          'Manage'
        )
      setProgramOptions(
        Object.keys(programWithPermissions)
          .filter((key) => programIdsWithPermission.includes(key))
          .map((key) => ({
            label: programWithPermissions[key],
            value: key,
          }))
      )
    }
  }

  const handleYesClick = async (
    payload: savePermissionSetsForUsersPayloadType
  ) => {
    try {
      dispatch(showLoader())
      if (props.isGlobalSet) {
        await saveGlobalPermissionSetsForUsers(payload)
      } else {
        await savePermissionSetsForUsers(payload)
      }
      let successMessage: string
      if (props.actionMode === NAMESPACES.CLONE_KEY) {
        successMessage = PERMISSION_ASSIGNMENT.CLONE_SUCCESS_MESSAGE
      } else if (props.actionMode === NAMESPACES.EDIT_KEY) {
        successMessage = PERMISSION_ASSIGNMENT.EDIT_SUCCESS_MESSAGE
      } else {
        successMessage = PERMISSION_ASSIGNMENT.SAVE_SUCCESS_MESSAGE
      }
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'success',
          message: successMessage,
        })
      )
      navigate(`/permission-assignment${props?.isGlobalSet ? '/global' : ''}`)
    } catch (err) {
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const error = (
          err as {
            response: {
              status: number
              data: {
                message: string
                code: string
                conflicts: {
                  program_id: number
                  user_id: number
                }[]
              }
            }
          }
        ).response

        if (error.status === HTTP_STATUS_CONSTANTS.CONFLICT) {
          dispatch(
            addToaster({
              id: uniqueId(),
              type: 'error',
              message: error?.data?.message,
            })
          )
          setErrors({
            user_id: {
              type: 'error',
              message: '',
            },
          })
          if (error?.data?.conflicts) {
            const results = error.data.conflicts.reduce(
              (acc, { program_id, user_id }) => {
                const id = dropdownOptions.find(
                  (option) => option.value === user_id.toString()
                )?.label
                if (id) {
                  if (!acc[id]) acc[id] = []
                  const program = programOptions.find(
                    (program) => program.value === program_id.toString()
                  )
                  if (program) acc[id].push(program.label)
                }
                return acc
              },
              {} as Record<string, string[]>
            )
            setConflictedClients(results)
          }
        }
      }
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const validatePayload = (payload: savePermissionSetsForUsersPayloadType) => {
    if (props.actionMode !== NAMESPACES.EDIT_KEY && !payload.users?.length) {
      dispatch(
        addToaster({
          id: uniqueId(),
          message: PERMISSION_ASSIGNMENT.USER_SELECT_MESSAGE,
          type: 'error',
        })
      )
      return false
    }
    if (!props?.isGlobalSet) {
      if (!payload.programs?.length) {
        dispatch(
          addToaster({
            type: 'error',
            id: uniqueId(),
            message: PERMISSION_ASSIGNMENT.ASSIGN_PERMISSION_MESSAGE,
          })
        )
        return false
      }
    }
    return true
  }
  const handleSave = async () => {
    const userData =
      props.actionMode === NAMESPACES.EDIT_KEY
        ? [{ user_id: userId ?? '' }]
        : selectedUsers?.map((item) => {
            return { user_id: item.value }
          })
    const permissionSetArray = Object.values(checkedPermissionSets).map(
      (permissionSetData) => ({
        permission_set_id: permissionSetData.permission_set_id,
        permission_set_name: permissionSetData.permission_set_name,
      })
    )
    const payload = {
      mode: props.actionMode === NAMESPACES.EDIT_KEY ? 'EDIT' : 'CREATE',
      users: userData,
      ...(!props?.isGlobalSet
        ? { programs: programPermissionSets }
        : { global_permission_sets: permissionSetArray }),
    }
    const isValid = validatePayload(payload)
    if (isValid) {
      setModal({
        open: true,
        message: PERMISSION_ASSIGNMENT.SAVE_CONFIRMATION_MESSAGE,
        onConfirm: () => {
          void handleYesClick(payload)
        },
      })
    }
  }

  const handleCancel = () => {
    setModal({
      open: true,
      message: 'Are you sure you want to discard the changes?',
      onConfirm: () =>
        navigate(
          `/permission-assignment${props?.isGlobalSet ? '/global' : ''}`
        ),
    })
  }
  const getMultiSelectDropdownOptions = (searchTerm: string) => {
    const data = users?.filter(
      (item) =>
        item.label
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) ||
        item.subLabel
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase())
    )
    setDropdownOptions(data ?? [])
  }

  const getPageTitle = () => {
    if (props.actionMode === NAMESPACES.VIEW_KEY) {
      return PERMISSION_ASSIGNMENT.VIEW_ASSIGNED_PERMISSIONS
    } else {
      return PERMISSION_ASSIGNMENT.ASSIGN_PERMISSION_SETS
    }
  }

  const fetchProgramsWithPermissionSets = async () => {
    try {
      const response = await getProgramsAndPermissionSets({ id: userId ?? '' })
      if (response.data) {
        const isAnyProgramEnabled = response.data.programs.some(
          (program: (typeof response.data.programs)[0]) =>
            program.enabled === true
        )
        setIsEditEnabled(isAnyProgramEnabled)
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (!isSuperAdmin && Object.keys(programPermissions).length === 0) return
    const fetchUsersDropdown = async () => {
      const programIdsWithPermission =
        programPermissionsObject.Permission_Set_Assignment_View(
          programPermissions,
          PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
          'View Only'
        )
      let response
      if (props?.isGlobalSet) {
        response = await getUnassignedUsersForGlobal(
          props.actionMode === NAMESPACES.CREATE_KEY ? true : false
        )
      } else {
        response = await getUnassignedUsers(
          isSuperAdmin ? [] : programIdsWithPermission,
          props.actionMode !== NAMESPACES.VIEW_KEY &&
            props.actionMode !== NAMESPACES.EDIT_KEY
        )
      }
      const dataResponse = response.data
      if (dataResponse) {
        if (
          props.actionMode === NAMESPACES.VIEW_KEY ||
          props.actionMode === NAMESPACES.EDIT_KEY
        ) {
          const viewLabelData = dataResponse.filter(
            (data: typeof dataResponse) => data.id.toString() === userId
          )
          viewFields.current = {
            userId: viewLabelData[0]?.user_id,
            firstName: viewLabelData[0]?.first_name,
            lastName: viewLabelData[0]?.last_name,
          }
        }
        const dropdownResponse = dataResponse?.map(
          (item: {
            user_id: string
            id: number
            first_name: string
            last_name: string
          }) => ({
            subLabel: item.user_id,
            label: item.first_name + ' ' + item.last_name,
            value: item.id.toString(),
          })
        )
        setUsers(dropdownResponse)
        setDropdownOptions(dropdownResponse)
      }
    }
    fetchUsersDropdown()
    if (props.actionMode === NAMESPACES.VIEW_KEY && !props.isGlobalSet) {
      fetchProgramsWithPermissionSets()
    }
    fetchPrograms()
    if (!isInitialLoad.current) {
      setRenderKey((prev) => !prev)
    }
    isInitialLoad.current = false
    setSelectedUsers([])
    setCheckedPermissionSets({})
  }, [programPermissions])

  useEffect(() => {
    if (
      props.actionMode !== NAMESPACES.EDIT_KEY &&
      props.actionMode !== NAMESPACES.VIEW_KEY
    ) {
      dispatch(
        setClientDropdown({
          isVisible: true,
          isGlobalVisible: false,
        })
      )
    }
  }, [location])

  if (!isSuperAdmin && Object.keys(programPermissions).length === 0) return null

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center px-3 py-2 border-b-2 flex-shrink-0">
        <div className="flex flex-col">
          <PageTitle title={getPageTitle()} subtitle="" />
        </div>

        <div className="flex items-center gap-2">
          {props.actionMode === NAMESPACES.CREATE_KEY ? null : (
            <div className="flex items-center gap-2">
              <div className="box-border rounded bg-secondary-50 text-xs flex justify-center pt-2 pb-2 px-4 pointer-events-none font-primary font-light">
                {selectedTenantName}
              </div>
              <SvgVerticalSplitter />
            </div>
          )}
          {props.actionMode === NAMESPACES.VIEW_KEY ? (
            <CustomButton
              isFilled={false}
              className="h-[2.375rem]"
              onClick={() => {
                navigate(
                  `/permission-assignment${props?.isGlobalSet ? '/global' : ''}`
                )
              }}
              dataQaId="assign-permission-back-button"
            >
              <span className="font-primary font-light text-xs">
                {NAMESPACES.BACK_BUTTON}
              </span>
            </CustomButton>
          ) : (
            <CustomButton
              isFilled={false}
              className="h-[2.375rem]"
              onClick={handleCancel}
              dataQaId="assign-permission-cancel-button"
            >
              <span className="font-primary font-light text-xs">
                {NAMESPACES.CANCEL_BUTTON}
              </span>
            </CustomButton>
          )}
          {props.actionMode !== NAMESPACES.VIEW_KEY ? (
            <CustomButton
              isFilled={true}
              className="h-[2.375rem]"
              onClick={handleSave}
              dataQaId="assign-permission-save-button"
            >
              <span className="font-primary font-light text-xs">
                {NAMESPACES.SAVE_BUTTON}
              </span>
            </CustomButton>
          ) : (
            <CustomButton
              isFilled={true}
              className="h-[2.375rem]"
              onClick={() =>
                navigate(
                  `/permission-assignment/${
                    props.isGlobalSet ? 'global/' : ''
                  }edit/${userId}`
                )
              }
              dataQaId="assign-permission-edit-button"
              disabled={!isEditEnabled}
            >
              <span className="font-primary font-light text-xs">
                {NAMESPACES.EDIT_BUTTON}
              </span>
            </CustomButton>
          )}
        </div>
      </div>
      <div
        className="sticky top-0 z-10 bg-white flex-shrink-0"
        key={renderKey.toString()}
      >
        {props.actionMode === NAMESPACES.VIEW_KEY ||
        props.actionMode === NAMESPACES.EDIT_KEY ? (
          <div className="flex justify-between m-2 mb-1 py-5 px-4 bg-blue-50 border-[0.5px] border-accent-300 rounded text-sm font-medium font-[Quicksand]">
            <span className="flex-1">
              User ID:{' '}
              <span className="text-accent-500">
                {viewFields.current.userId}
              </span>{' '}
            </span>
            <span className="flex-1">
              First Name:{' '}
              <span className="text-accent-500">
                {viewFields.current.firstName}
              </span>
            </span>
            <span className="flex-1">
              Last Name:{' '}
              <span className="text-accent-500">
                {viewFields.current.lastName}
              </span>
            </span>
          </div>
        ) : (
          <div className="border-b px-3 py-4.5">
            <div className="w-[35rem]" ref={divRef}>
              <MultiSelectSearchDropDown
                label="User ID"
                name="user_id"
                options={dropdownOptions}
                noOfChips={2}
                onSearch={getMultiSelectDropdownOptions}
                defaultSelected={selectedUsers}
                onChange={(e) => setSelectedUsers(e)}
                dataQaId="selected-users"
                errors={errors}
                excludedParentRef={divRef}
                allowCustomValues={false}
              />
            </div>
            {Object.keys(conflictedClients)?.length ? (
              <div className="border rounded mt-[1px] p-2 w-[35rem]">
                <span className="flex gap-2 items-center">
                  <SvgInfo className="svg-path-fill-error-500" />
                  <span className="text-xs">
                    {PERMISSION_ASSIGNMENT.RECORD_CONFLICT_MESSAGE}
                  </span>
                </span>
                <ul className="max-h-[6.5rem] overflow-y-auto">
                  {Object.keys(conflictedClients).map((client, index) => (
                    <li
                      key={`${client}-${index}`}
                      className="flex items-center my-1 ml-2 text-xs"
                    >
                      <div className="bg-error-500 inline-block h-1 w-1 rounded mr-2" />
                      <span className="text-error-500">{client}</span> &nbsp;-{' '}
                      {conflictedClients[client].join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
        {props.actionMode !== NAMESPACES.VIEW_KEY ? (
          <div className="flex px-3 pt-3">
            <PageTitle
              title={
                props?.isGlobalSet
                  ? PERMISSION_ASSIGNMENT.SELECT_GLOBAL_HEADING
                  : PERMISSION_ASSIGNMENT.SELECT_PROGRAMS_HEADING
              }
              className="text-sm"
              subtitle=""
            />
          </div>
        ) : null}
        <AssignPermissionSet
          programPermissionSets={programPermissionSets}
          setProgramPermissionSets={setProgramPermissionSets}
          actionMode={props.actionMode}
          programOptions={programOptions}
          isGlobalSet={props?.isGlobalSet}
          setCheckedPermissionSets={setCheckedPermissionSets}
          checkedPermissionSets={checkedPermissionSets}
        />
      </div>
      {modal.open && (
        <ConfirmDialog
          open={modal.open}
          message={modal.message}
          onConfirm={modal.onConfirm}
          onCancel={() => setModal(initialModalData)}
        />
      )}
    </div>
  )
}
